import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import { readNorthstarJson } from '../api/northstarRequest';
import NorthstarResults from './NorthstarResults';

const endpoint = `${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}/quality/station-runs/northstar-pilot`;
const empty = scope => ({ scope, runs: null, detail: null, error: '', busy: false, nextBefore: null });
const sourceLabel = run => run.mode === 'simulation' ? 'Synthetic simulation' : run.submissionSource === 'browser-submission'
  ? 'Browser acquisition · client reported' : 'Imported report · client reported';

export default function NorthstarHistory({ serviceTag }) {
  const { getToken, userId, sessionId } = useAuth();
  const scope = JSON.stringify([serviceTag, userId, sessionId]);
  const [record, setRecord] = useState(() => empty(scope));
  const data = record.scope === scope ? record : empty(scope);
  const request = useRef(0), controller = useRef(null);
  useEffect(() => {
    request.current += 1; controller.current?.abort(); setRecord(empty(scope));
    return () => { request.current += 1; controller.current?.abort(); };
  }, [scope]);
  const load = useCallback(async (id, before) => {
    const current = ++request.current;
    controller.current?.abort(); controller.current = new AbortController();
    setRecord({ ...empty(scope), busy: true });
    try {
      const token = await getToken();
      if (current !== request.current) return;
      if (!token) throw new Error('Sign in to view Northstar Test records.');
      const suffix = id ? `/${encodeURIComponent(id)}` : `?subjectServiceTag=${encodeURIComponent(serviceTag)}${before ? `&before=${encodeURIComponent(before)}` : ''}`;
      const body = await readNorthstarJson(endpoint + suffix, { signal: controller.current.signal, headers: { Authorization: `Bearer ${token}` } });
      if (current === request.current) setRecord({ ...empty(scope), ...(id ? { detail: body.run } : { runs: body.runs || [], nextBefore: body.pagination?.nextBefore || null }) });
    } catch (err) {
      if (current === request.current && err.name !== 'AbortError') setRecord({ ...empty(scope), error: err.message });
    }
  }, [getToken, serviceTag, scope]);
  if (!serviceTag) return null;
  const report = data.detail?.report;
  return <section className="ni-panel" style={{ padding: '1rem', marginTop: '1rem' }}>
    <h3 style={{ marginTop: 0 }}>Northstar · Test calibration</h3>
    <p style={{ color: 'var(--ni-text-muted)' }}>Experimental measurements linked to {serviceTag}. These records do not release the unit or change its product family.</p>
    <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
      <button className="ni-button" disabled={data.busy} onClick={() => load()}>{data.busy ? 'Loading…' : 'Load Test records'}</button>
      <Link className="ni-button" to={`/quality/northstar/browser?serviceTag=${encodeURIComponent(serviceTag)}`}>Start browser Test calibration</Link>
      <Link className="ni-button" to={`/quality/northstar?serviceTag=${encodeURIComponent(serviceTag)}`}>Import desktop report</Link>
    </div>
    {data.error && <p role="alert">{data.error}</p>}
    {data.runs && !data.runs.length && <p>No Northstar Test records found.</p>}
    {!!data.runs?.length && <div className="ni-table-wrap" style={{ marginTop: '1rem' }}><table className="ni-table">
      <thead><tr><th>Received</th><th>Source</th><th>Test outcome</th><th>Attempt</th></tr></thead>
      <tbody>{data.runs.map(run => <tr key={run.id}><td>{new Date(run.receivedAt).toLocaleString()}</td><td>{sourceLabel(run)}</td><td>{run.outcome}</td>
        <td><button className="ni-button" disabled={data.busy} onClick={() => load(run.id)}>Open record</button></td></tr>)}</tbody>
    </table></div>}
    {data.nextBefore && <button className="ni-button" disabled={data.busy} onClick={() => load(undefined, data.nextBefore)}>Older Test records</button>}
    {report && <div style={{ marginTop: '1rem' }}>
      <h4>Recorded Test evidence</h4><p>{sourceLabel({ ...data.detail, mode: report.mode })} · Test outcome: {report.outcome || 'Unavailable'}</p>
      <p>Profile: {report.profile?.id || 'Unavailable'} · Revision {report.profile?.revision || 'Unavailable'}</p>
      <p>Measurements: {report.measurements?.length || 0} · Tables: {report.tables?.length || 0} · Operator confirmations: {report.confirmations?.length || 0}</p>
      <p style={{ color: 'var(--ni-text-muted)' }}>Captured or simulated inputs and calculated results remain identified by the recorded acquisition mode. Physical setup, loading and independent capture confirmations are operator attestations.</p>
      <NorthstarResults report={report} />
      {!!report.tables?.length && <ul>{report.tables.map(table => <li key={table.key}>{table.key} · {table.rows.length} rows · {table.columns.join(', ')}</li>)}</ul>}
    </div>}
    {data.detail && <details style={{ marginTop: '1rem' }}><summary>Complete recorded report and evidence hashes</summary>
      <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 550, overflow: 'auto', fontSize: '.8rem' }}>{JSON.stringify(data.detail, null, 2)}</pre>
    </details>}
  </section>;
}
